import React from 'react';
import { Download, Eye, FileText, Folder, Star } from 'lucide-react';

import SixDetailCloneStatusIndicator from '@/components/organism/SixDetail/SixDetailCloneStatusIndicator';
import type { SixDetailFileBrowserRowProps } from '@/components/organism/SixDetail/SixDetailFileBrowser/SixDetailFileBrowser.types';
import SixDetailFileBrowserRowActions from '@/components/organism/SixDetail/SixDetailFileBrowser/SixDetailFileBrowserRowActions';
import { useFileCanDownload } from '@/hooks/poc/useFileCloneStatus';
import { useSixDetailFileBrowserRowDnd } from '@/hooks/sixDetail/useSixDetailFileBrowserRowDnd';
import { classNames } from '@/utils/classNames.utils';

import { getCreatedByLabel, getPathLabel } from './fileBrowserRow.utils';

function SixDetailFileBrowserDesktopRow({
  item,
  showPathColumn,
  isSelected,
  canShowRowActions,
  onItemAction,
  onToggleSelect,
  selectedIds,
  isDragDisabled,
}: SixDetailFileBrowserRowProps): React.ReactElement {
  const canDownload = useFileCanDownload(item);
  const {
    attributes,
    listeners,
    setRowRef,
    isDragging,
    isOver,
    isFolder,
    isRowDragDisabled,
  } = useSixDetailFileBrowserRowDnd({
    item,
    isSelected,
    selectedIds,
    isDragDisabled,
    dragIdPrefix: 'drag:',
    droppableIdPrefix: '',
  });

  function handleRowClick(): void {
    if (item.kind === 'folder') {
      onItemAction(item, 'open');
      return;
    }

    onToggleSelect(item.id);
  }

  function handleActionClick(
    event: React.MouseEvent<HTMLButtonElement>,
    action: string,
  ): void {
    event.stopPropagation();
    onItemAction(item, action);
  }

  return (
    <div
      ref={setRowRef}
      {...attributes}
      {...listeners}
      className={classNames(
        'grid items-center gap-3 border-b border-border px-4 py-3 transition-colors',
        showPathColumn ? 'grid-cols-14' : 'grid-cols-12',
        isSelected ? 'bg-primary/5' : 'bg-background hover:bg-muted/40',
        !isRowDragDisabled && 'touch-action-none',
        isDragging && 'opacity-40',
        isOver && isFolder && 'bg-primary/10 ring-1 ring-inset ring-primary/40',
        'cursor-pointer',
      )}
      onClick={handleRowClick}
    >
      <div className={classNames('min-w-0', showPathColumn ? 'col-span-4' : 'col-span-6')}>
        <div className="flex items-center gap-2 min-w-0">
          {item.kind === 'folder' ? (
            <Folder className="h-4 w-4 flex-shrink-0 text-primary" />
          ) : (
            <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          )}
          <span className="truncate text-sm font-medium text-foreground">{item.name}</span>
        </div>
        {item.kind === 'file' && (
          <div className="mt-1 pl-6">
            <SixDetailCloneStatusIndicator file={item} />
          </div>
        )}
      </div>

      {showPathColumn && (
        <div className="col-span-3 truncate text-sm text-muted-foreground">{getPathLabel(item)}</div>
      )}
      <div className="col-span-1 text-sm text-muted-foreground">{item.size ?? '-'}</div>
      <div className="col-span-2 text-sm text-muted-foreground">{item.modified}</div>
      <div className={classNames('truncate text-sm text-muted-foreground', showPathColumn ? 'col-span-3' : 'col-span-2')}>
        {getCreatedByLabel(item)}
      </div>

      <div
        className="flex items-center justify-end gap-1"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {item.kind === 'file' && (
          <>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={item.isFavorite === true ? 'Remove from favorites' : 'Add to favorites'}
              onClick={(event) => {
                handleActionClick(event, 'favorite');
              }}
            >
              <Star className={classNames('h-4 w-4', item.isFavorite === true && 'fill-current text-amber-500')} />
            </button>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="View details"
              onClick={(event) => {
                handleActionClick(event, 'view');
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={classNames(
                'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                canDownload
                  ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  : 'cursor-not-allowed text-muted-foreground/50',
              )}
              aria-label="Download"
              disabled={!canDownload}
              onClick={(event) => {
                if (!canDownload) return;
                handleActionClick(event, 'download');
              }}
            >
              <Download className="h-4 w-4" />
            </button>
          </>
        )}

        <SixDetailFileBrowserRowActions
          item={item}
          canShowRowActions={canShowRowActions}
          onItemAction={onItemAction}
        />
      </div>
    </div>
  );
}

export default SixDetailFileBrowserDesktopRow;
